import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import ApsModelViewer from './ApsModelViewer';

const extColor = (ext) => {
  if (['nwd', 'nwc', 'nwf'].includes(ext)) return '#0f766e';
  if (['rvt', 'ifc'].includes(ext)) return '#1d4ed8';
  if (['glb', 'gltf', 'fbx'].includes(ext)) return '#7c3aed';
  return '#64748b';
};

/**
 * Schedule → Model (also Bid Package → Proposal Schedule → Model)
 * Discovers 3D models from SharePoint. Backend prepares the main viewable model
 * in the background; the Viewer only opens when Ready.
 */
function modelsApiBase({ jobNumber, estimateNumber }) {
  if (jobNumber) return `/schedule-models/${encodeURIComponent(jobNumber)}`;
  if (estimateNumber) return `/schedule-models/estimate/${encodeURIComponent(estimateNumber)}`;
  return null;
}

const ScheduleModel = ({ jobNumber, estimateNumber }) => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [viewer, setViewer] = useState(null); // { urn, accessToken, expiresIn, title }
  const [toast, setToast] = useState(null);
  const pollRef = useRef(null);
  const dataRef = useRef(null);
  const apiBase = modelsApiBase({ jobNumber, estimateNumber });
  dataRef.current = data;

  const load = useCallback(async ({ quiet = false, poll = false, rescan = false } = {}) => {
    if (!apiBase) return;
    try {
      if (!quiet && !dataRef.current) {
        setLoading(true);
        setError(null);
      }
      const qs = rescan ? '?rescan=1' : poll ? '?poll=1' : '';
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${apiBase}${qs}`, null, {
        timeout: 20000
      });
      setData(res.data.data);
      setError(null);
    } catch (e) {
      console.error('Failed to load schedule models:', e);
      const msg = e.response?.data?.error || e.message || 'Failed to find models';
      if (!quiet && !dataRef.current) setError(msg);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [instance, accounts, apiBase]);

  useEffect(() => { load(); }, [load]);

  // Show first NWD hits while SharePoint is still being walked
  useEffect(() => {
    if (!data?.scanning) return undefined;
    const id = setInterval(() => {
      load({ quiet: true, poll: true });
    }, 2000);
    return () => clearInterval(id);
  }, [data?.scanning, load]);

  // Quiet poll while anything is preparing — cache only, do not re-walk Graph
  useEffect(() => {
    const preparing = (data?.models || []).some((m) =>
      ['pending', 'uploading', 'translating'].includes(m.viewStatus)
    );
    if (!preparing || data?.scanning) return undefined;
    const id = setInterval(() => {
      load({ quiet: true, poll: true });
    }, 4000);
    pollRef.current = id;
    return () => {
      clearInterval(id);
      if (pollRef.current === id) pollRef.current = null;
    };
  }, [data, load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const openDownload = async (model) => {
    try {
      setBusyId(model.id);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `${apiBase}/download?driveId=${encodeURIComponent(model.driveId)}&itemId=${encodeURIComponent(model.id)}`
      );
      const url = res.data?.data?.url || res.data?.data?.webUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else throw new Error('No download URL returned');
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Download failed');
    } finally {
      setBusyId(null);
    }
  };

  const openView = async (model) => {
    try {
      setBusyId(`view-${model.id}`);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `${apiBase}/view`,
        { driveId: model.driveId, itemId: model.id }
      );
      const st = res.data?.data;
      if (st?.status === 'ready' && st.urn && st.accessToken) {
        setViewer({
          urn: st.urn,
          accessToken: st.accessToken,
          expiresIn: st.expiresIn || 3600,
          title: model?.name || st.fileName || '3D Model'
        });
        return;
      }
      if (st?.status === 'not_configured' || st?.status === 'unsupported') {
        setToast(st.error || 'Cannot view this file in the portal yet');
        return;
      }
      if (st?.status === 'failed') {
        setToast(st.error || 'Preparation failed — retrying in background');
        // force retry
        await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          `${apiBase}/view`,
          { driveId: model.driveId, itemId: model.id, force: true }
        );
        load({ quiet: true });
        return;
      }
      setToast('Preparing in the background — Open unlocks when Ready');
      load({ quiet: true });
    } catch (e) {
      setToast(e.response?.data?.error || e.message || 'Failed to open viewer');
    } finally {
      setBusyId(null);
    }
  };

  const refreshViewerToken = useCallback(async () => {
    const res = await authenticatedApiCall(instance, accounts, 'GET', '/schedule-models/viewer-token');
    return res.data.data;
  }, [instance, accounts]);

  if (!apiBase) {
    return <div style={{ padding: 24 }}>Select a project or estimate to browse its 3D models.</div>;
  }

  const models = [...(data?.models || [])].sort((a, b) => {
    if (Boolean(a.viewReady) !== Boolean(b.viewReady)) return a.viewReady ? -1 : 1;
    return 0;
  });
  const anyReady = models.some((m) => m.viewReady);
  const anyPreparing = models.some((m) =>
    ['pending', 'uploading', 'translating'].includes(m.viewStatus)
  );

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a' }}>3D Model</h2>
        <span style={{ color: '#64748b', fontSize: 13 }}>
          {jobNumber || data?.jobNumber ? `Job ${jobNumber || data.jobNumber}` : 'Proposal'}
          {(data?.estimateNumber || estimateNumber)
            ? ` · Estimate ${data?.estimateNumber || estimateNumber}`
            : ''}
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => load({ quiet: Boolean(data), rescan: true })}
          disabled={loading || Boolean(data?.scanning)}
          style={btnStyle}
        >
          {loading || data?.scanning ? 'Scanning…' : '↻ Rescan SharePoint'}
        </button>
        <button type="button" onClick={() => setShowHelp(true)} style={btnStyle} title="Help">❓ Help</button>
      </div>

      <p style={{ margin: '0 0 16px', color: '#475569', fontSize: 14, lineHeight: 1.5, maxWidth: 820 }}>
        Looks in the Estimates folder (root files included) and under
        <strong>Schedule → Basis of Schedule → 10. 3D Model</strong>
        {jobNumber ? ' on the job site.' : ' (and the job site if this estimate is awarded).'}
        The first NWD that turns up is listed immediately; the rest keep arriving while SharePoint is searched.
        <strong> Open</strong> paints a Ready NWD from <strong>live P6 physical %</strong> (or tracker %).
        Download is still the path into Navisworks Simulate / YR Tools / TimeLiner.
      </p>

      {data && data.apsConfigured === false && (
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          color: '#92400e',
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          lineHeight: 1.5
        }}>
          <strong>Viewer not configured yet.</strong> Add a free Autodesk APS app
          (<a href="https://aps.autodesk.com/myapps" target="_blank" rel="noreferrer">aps.autodesk.com/myapps</a>)
          and set <code>APS_CLIENT_ID</code> / <code>APS_CLIENT_SECRET</code> in the backend <code>.env</code>, then restart.
          Download still works without that.
        </div>
      )}

      {anyReady && (
        <div style={{
          background: '#ecfdf5',
          border: '1px solid #6ee7b7',
          color: '#065f46',
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          lineHeight: 1.5
        }}>
          <strong>A model is Ready.</strong> Click <strong>Open</strong> on the green row.
          Newer files under 10. 3D Model stay listed — use Prepare only if you need that exact revision.
        </div>
      )}

      {data?.scanning && (
        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          color: '#1e3a8a',
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          lineHeight: 1.5
        }}>
          {models.length
            ? <>Found <strong>{models.length}</strong> model{models.length === 1 ? '' : 's'} — still searching SharePoint for more…</>
            : 'Searching SharePoint for NWD / NWC / Revit files…'}
        </div>
      )}

      {anyPreparing && (
        <div style={{
          background: '#f0fdfa',
          border: '1px solid #99f6e4',
          color: '#134e4a',
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13
        }}>
          Preparing in the background — you can leave this page. When the badge says <strong>Ready</strong>, click <strong>Open</strong>.
        </div>
      )}

      {toast && (
        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          color: '#1e3a8a',
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13
        }}>
          {toast}
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ color: '#64748b', padding: '40px 0', textAlign: 'center' }}>
          Scanning job site and Estimates folder for 3D models…
        </div>
      )}

      {data?.scanning && models.length === 0 && !loading && (
        <div style={{ color: '#64748b', padding: '24px 0', textAlign: 'center' }}>
          Looking for the first NWD — it will appear here as soon as SharePoint returns it.
        </div>
      )}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {(data.sources || []).map((s, i) => (
              <span
                key={i}
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: s.status === 'ok' ? '#ecfdf5' : '#f8fafc',
                  border: `1px solid ${s.status === 'ok' ? '#a7f3d0' : '#e2e8f0'}`,
                  color: s.status === 'ok' ? '#065f46' : '#64748b'
                }}
                title={s.error || ''}
              >
                {s.type === 'job' ? `Job · ${s.driveName || s.sitePath}` : `Estimate · ${s.folderName || s.estimateNumber || '—'}`}
                {s.status === 'ok' && s.count != null ? ` (${s.count})` : ''}
                {s.status !== 'ok' && s.status !== undefined ? ` — ${s.status.replace(/_/g, ' ')}` : ''}
              </span>
            ))}
          </div>
          {(data.sources || []).some((s) => s.error) && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '8px 12px',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 12,
              lineHeight: 1.45
            }}>
              {(data.sources || []).filter((s) => s.error).map((s, i) => (
                <div key={i}>{s.error}</div>
              ))}
            </div>
          )}

          {models.length === 0 && data.scanning ? null : models.length === 0 ? (
            <div style={{
              border: '1px dashed #cbd5e1',
              borderRadius: 12,
              padding: '36px 24px',
              textAlign: 'center',
              color: '#64748b',
              background: '#f8fafc'
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
              <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>No 3D model files found yet</div>
              <div style={{ fontSize: 13, maxWidth: 480, margin: '0 auto' }}>
                Drop an <code>.nwd</code> / <code>.nwc</code> / <code>.rvt</code> / <code>.ifc</code> into the job
                Team library or the Estimates project folder, then hit Rescan.
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {models.map((m) => {
                const canView = m.canViewInPortal !== false && ['nwd', 'nwc', 'rvt', 'ifc'].includes(m.ext);
                const ready = Boolean(m.viewReady);
                const preparing = ['pending', 'uploading', 'translating'].includes(m.viewStatus);
                const failed = m.viewStatus === 'failed';
                return (
                  <div
                    key={`${m.driveId}-${m.id}`}
                    style={{
                      display: 'flex',
                      gap: 14,
                      alignItems: 'center',
                      padding: '14px 16px',
                      border: `1px solid ${ready ? '#a7f3d0' : '#e2e8f0'}`,
                      borderRadius: 10,
                      background: ready ? '#f0fdf4' : '#fff'
                    }}
                  >
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 10,
                      background: extColor(m.ext),
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      flexShrink: 0
                    }}>
                      .{m.ext}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{m.name}</span>
                        {ready && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '2px 8px', borderRadius: 999 }}>
                            Ready
                          </span>
                        )}
                        {preparing && !ready && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999 }}>
                            Preparing{m.viewProgressPct != null ? ` · ${Math.round(m.viewProgressPct)}%` : '…'}
                          </span>
                        )}
                        {failed && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', background: '#fee2e2', padding: '2px 8px', borderRadius: 999 }} title={m.viewError || ''}>
                            Failed — click Retry
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', wordBreak: 'break-all' }}>
                        {m.source === 'job' ? 'Job site' : 'Estimates'} · {m.driveName} · {m.path}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                        {m.sizeLabel}
                        {m.lastModified ? ` · ${new Date(m.lastModified).toLocaleString()}` : ''}
                        {preparing && m.viewProgress ? ` · ${m.viewProgress}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {canView && ready && (
                        <button
                          type="button"
                          onClick={() => openView(m)}
                          disabled={busyId === `view-${m.id}`}
                          style={{ ...btnStyle, background: '#0f766e', color: '#fff', border: 'none' }}
                        >
                          {busyId === `view-${m.id}` ? '…' : 'Open'}
                        </button>
                      )}
                      {canView && preparing && !ready && (
                        <button
                          type="button"
                          onClick={() => openView(m)}
                          disabled={busyId === `view-${m.id}`}
                          title="Backend is preparing this model. Click to nudge / resume if stuck."
                          style={{ ...btnStyle, background: '#0f766e', color: '#fff', border: 'none', opacity: 0.85 }}
                        >
                          {busyId === `view-${m.id}` ? '…' : 'Preparing…'}
                        </button>
                      )}
                      {canView && !ready && !preparing && (
                        <button
                          type="button"
                          onClick={() => openView(m)}
                          disabled={busyId === `view-${m.id}`}
                          style={{ ...btnStyle, background: failed ? '#b45309' : '#0f766e', color: '#fff', border: 'none' }}
                        >
                          {busyId === `view-${m.id}` ? '…' : failed ? 'Retry' : 'Prepare'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openDownload(m)}
                        disabled={busyId === m.id}
                        style={btnStyle}
                      >
                        {busyId === m.id ? '…' : 'Download'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {data.extras?.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#0f172a' }}>YR Tools / TimeLiner extras</h3>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', maxWidth: 720 }}>
                Plugin packages and recorded TimeLiner videos from the job SharePoint (usually Basis of Schedule → Model).
                These do not open in the browser — download for Simulate.
              </p>
              <div style={{ display: 'grid', gap: 10 }}>
                {data.extras.map((m) => (
                  <div
                    key={`extra-${m.driveId}-${m.id}`}
                    style={{
                      display: 'flex',
                      gap: 14,
                      alignItems: 'center',
                      padding: '12px 16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      background: '#fff'
                    }}
                  >
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 10,
                      background: m.extraKind === 'video' ? '#7c3aed' : '#b45309',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 11,
                      textTransform: 'uppercase',
                      flexShrink: 0
                    }}>
                      .{m.ext}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{m.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b', wordBreak: 'break-all' }}>
                        {m.source === 'job' ? 'Job site' : 'Estimates'} · {m.path}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                        {m.viewerHint}
                        {m.sizeLabel ? ` · ${m.sizeLabel}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => openDownload(m)}
                      disabled={busyId === m.id}
                      style={btnStyle}
                    >
                      {busyId === m.id ? '…' : 'Download'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {showHelp && (
        <div
          onClick={() => setShowHelp(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '85vh', overflow: 'auto' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid #e2e8f0', background: '#0f766e', color: '#fff', borderRadius: '12px 12px 0 0' }}>
              <strong style={{ flex: 1 }}>❓ Schedule Model — Help</strong>
              <ListenButton compact guideId="schedule-model-help" title="Schedule Model Help" accent="#0f766e" dark />
              <button type="button" onClick={() => setShowHelp(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 18, fontSize: 14, color: '#334155', lineHeight: 1.55 }}>
              <HelpWithVideoTour guideId="schedule-model-help" title="Schedule Model Help" accent="#0f766e">
              <p><strong>Where files come from.</strong> Job Team library, then Estimates. On 71940 that is <code>04. Schedule / 00. Basis of Schedule / 10. 3D Model (View Point )</code> — <code>03. NWD</code> for the coordination model, <code>05. Plugin</code> for YR Tools, <code>04. AVI</code> for the TimeLiner recording. Superseded copies are skipped. The first NWD is listed as soon as SharePoint returns it; the rest keep arriving. A later visit shows those hits immediately.</p>
              <p><strong>Open = live colour.</strong> Ready models open in Autodesk Viewer. The side dock reads live Primavera (Current / dated XER) and paints Complete / In progress / Not started / Late. Default is P6 physical %. <strong>Tracker %</strong> uses the hours-weighted field/shop rollup on the same Activity IDs (P6 % stays on the row). Click a legend colour to isolate those pieces (several colours are an OR). <strong>3-week look-ahead</strong> keeps activities whose planned dates overlap the P6 data date plus 21 days, plus late remaining work (complete stays out), and isolates their components. Click an Activity ID to isolate one row. Planned 4D uses planned start and finish only — no as-built playback in the browser. <strong>Show all</strong> clears the chips and the look-ahead.</p>
              <p><strong>How the join works.</strong> We search the translation for Activity ID (and Item Name / Tag). That is the same key YR Tools already used in Simulate. The match is saved (this job + this Autodesk file). Next Open paints immediately; only <strong>Refresh matching</strong> starts over. Switching P6 / Tracker % recolours the saved map. If nothing matches, we do not grey the whole plant — we say so, and Download stays the path into the desktop plugin.</p>
              <p><strong>Background prepare.</strong> Opening this page queues the main <code>.nwd</code> / <code>.nwc</code> / <code>.rvt</code> / <code>.ifc</code> for Autodesk translation. Leave and come back. Open unlocks when the badge says Ready.</p>
              <p><strong>YR Tools / TimeLiner.</strong> The plugin lives on the laptop and in that SharePoint Model folder. TimeLiner simulation is already proven there. Next slice: a portal-fed TimeLiner data source so Simulate does not need a CSV. The AVI is the recorded result, not a live colour engine.</p>
              <p><strong>Autodesk Build and Procore.</strong> Those sidebar pages are doors into the client&apos;s RFIs, drawings, and files. They are not where we colour the model. Paste the client project URL once; Open jumps to their tool.</p>
              <p><strong>Still to do (follow-ups).</strong> Write Activity ID onto items from the portal; TimeLiner DataSourceProvider in YR Tools v2 (source beside this repo); check in the 71940 plugin ZIP so agents can evolve it. Bake-and-publish into ACC/Procore is off the table unless a client refuses the portal.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {viewer && (
        <ApsModelViewer
          urn={viewer.urn}
          accessToken={viewer.accessToken}
          expiresIn={viewer.expiresIn}
          title={viewer.title}
          jobNumber={jobNumber || data?.jobNumber || null}
          onClose={() => setViewer(null)}
          onTokenRefresh={refreshViewerToken}
        />
      )}
    </div>
  );
};

const btnStyle = {
  border: '1px solid #cbd5e1',
  background: '#fff',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer',
  color: '#334155'
};

export default ScheduleModel;
